<template>
  <Teleport to="body">
    <div class="bpmn-modal-overlay" @click.self="$emit('close')">
      <div class="bpmn-modal">
        <!-- Шапка -->
        <div class="bpmn-modal__header">
          <div class="bpmn-modal__title-row">
            <span class="bpmn-modal__title">Схема процесса приёмки</span>
            <span v-if="instance" class="instance-badge" :class="'instance-badge--' + instance.status">
              {{ instance.status === 'active' ? 'Активен' : 'Завершён' }}
            </span>
          </div>
          <div v-if="instance" class="bpmn-modal__meta">
            <span>Процесс #<b>{{ instance.process_instance_id }}</b></span>
            <span>Схема <b>{{ instance.schema_id }}</b></span>
            <span>Этаж <b>{{ instance.floor_map_id }}</b></span>
            <span>Запущен: <b>{{ formatDate(instance.started_at) }}</b></span>
            <span v-if="instance.current_node_labels?.length" class="bpmn-modal__current">
              📍 {{ instance.current_node_labels.join(' · ') }}
            </span>
          </div>
          <div class="bpmn-modal__actions">
            <button class="bpmn-modal__refresh" @click="load">↻ Обновить</button>
            <button class="bpmn-modal__close" @click="$emit('close')">✕</button>
          </div>
        </div>

        <!-- Тело -->
        <div class="bpmn-modal__body">
          <div v-if="loading" class="bpmn-modal__state">Загрузка...</div>
          <div v-else-if="error" class="bpmn-modal__state bpmn-modal__state--error">{{ error }}</div>
          <div v-else-if="!instance" class="bpmn-modal__state bpmn-modal__state--empty">
            <div class="bpmn-modal__empty-icon">📋</div>
            <div>Активный процесс не найден</div>
            <div class="bpmn-modal__empty-sub">Для этой группы работ процесс ещё не запущен</div>
          </div>
          <BpmnFlow
            v-else
            :schema-id="instance.schema_id"
            :current-node-ids="instance.current_bpmn_node_ids || []"
            :completed-node-ids="instance.completed_bpmn_node_ids || []"
            :instance-nodes="instanceNodes"
            :readonly="true"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import BpmnFlow from './BpmnFlow.vue'
import { wcheckBpmnAPI } from './services/api'
import { useBpmnDicts } from './useBpmnDicts'

const props = defineProps({
  workSetCheckGroupId: { type: Number, required: true },
  floorMapId: { type: Number, required: true },
  placementTypeId: { type: Number, required: true },
})

defineEmits(['close'])

const bpmnDicts = useBpmnDicts()

const instance = ref(null)
const instanceNodes = ref([])
const loading = ref(false)
const error = ref(null)

async function load() {
  loading.value = true
  error.value = null
  try {
    const res = await wcheckBpmnAPI.getInstanceByKeys(
      props.workSetCheckGroupId,
      props.floorMapId,
      props.placementTypeId,
    )
    instance.value = res?.data || null
    if (instance.value) {
      const nodesRes = await wcheckBpmnAPI.getInstanceNodes(instance.value.process_instance_id)
      instanceNodes.value = nodesRes?.data || []
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка загрузки'
  } finally {
    loading.value = false
  }
}

function formatDate(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('ru-RU')
}

onMounted(() => {
  bpmnDicts.load()
  load()
})
</script>

<style scoped>
.bpmn-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.bpmn-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 1300px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
}

.bpmn-modal__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.bpmn-modal__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bpmn-modal__title {
  font-size: 17px;
  font-weight: 700;
  color: #212121;
}

.instance-badge {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}
.instance-badge--active    { background: #c8e6c9; color: #2e7d32; }
.instance-badge--completed { background: #e0e0e0; color: #616161; }

.bpmn-modal__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 13px;
  color: #546e7a;
}

.bpmn-modal__current {
  color: #f57c00;
  font-weight: 600;
}

.bpmn-modal__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  top: 14px;
  right: 18px;
}

.bpmn-modal__header {
  position: relative;
}

.bpmn-modal__refresh {
  padding: 5px 12px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #1565c0;
}
.bpmn-modal__refresh:hover { background: #bbdefb; }

.bpmn-modal__close {
  width: 30px;
  height: 30px;
  border: none;
  background: #f5f5f5;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
}
.bpmn-modal__close:hover { background: #ffebee; color: #c62828; }

.bpmn-modal__body {
  flex: 1;
  overflow: auto;
  padding: 16px 20px 20px;
  min-height: 0;
}

.bpmn-modal__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 300px;
  font-size: 14px;
  color: #757575;
}
.bpmn-modal__state--error { color: #c62828; }
.bpmn-modal__state--empty { color: #9e9e9e; }

.bpmn-modal__empty-icon {
  font-size: 40px;
  line-height: 1;
}
.bpmn-modal__empty-sub {
  font-size: 12px;
  color: #bdbdbd;
}
</style>
